import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { shiftService, siteService, userService } from '../../services';
import { Shift, Site, User, SiteFilter } from '../../types';
import { MONTH_NAMES, DAY_NAMES, SHIFT_STATUS_COLORS, DEFAULT_PAGE_SIZE } from '../../constants';
import { useDateFormatter } from '../../hooks';
import styles from './Calendar.module.css';

interface CalendarDay {
  date: Date;
  shifts: Shift[];
  isCurrentMonth: boolean;
  isToday: boolean;
}

export const Calendar: React.FC = () => {
  const { user } = useAuth();
  const { formatTime, formatLongDate } = useDateFormatter();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  // Filtri
  const [siteFilter, setSiteFilter] = useState<number | ''>('');
  const [employeeFilter, setEmployeeFilter] = useState<number | ''>('');
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);

  // Memoize loadShifts to prevent recreation on every render
  const loadShifts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const filter: {
        startDate: string;
        endDate: string;
        page: number;
        pageSize: number;
        employeeId?: number;
        siteId?: number;
      } = {
        startDate: startOfMonth.toISOString().split('T')[0],
        endDate: endOfMonth.toISOString().split('T')[0],
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE.SHIFTS,
      };

      // Employee vede solo i propri turni
      if (user?.role === 3 && user.id) {
        filter.employeeId = user.id;
      } else {
        // Applica filtri solo per non-employee
        if (siteFilter !== '') {
          filter.siteId = siteFilter;
        }
        if (employeeFilter !== '') {
          filter.employeeId = employeeFilter;
        }
      }

      const response = await shiftService.getShifts(filter);
      setShifts(response.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento dei turni';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [currentDate, user?.role, user?.id, siteFilter, employeeFilter]);

  // Create a Map for faster shift lookups by date
  const shiftsByDate = useMemo(() => {
    const map = new Map<string, Shift[]>();
    shifts.forEach((shift) => {
      const dateStr = new Date(shift.startTime).toISOString().split('T')[0];
      if (!map.has(dateStr)) {
        map.set(dateStr, []);
      }
      map.get(dateStr)!.push(shift);
    });
    return map;
  }, [shifts]);

  // Memoize getShiftsForDate using the Map for O(1) lookups
  const getShiftsForDate = useCallback(
    (date: Date): Shift[] => {
      const dateStr = date.toISOString().split('T')[0];
      return shiftsByDate.get(dateStr) || [];
    },
    [shiftsByDate]
  );

  // Memoize generateCalendar to prevent recreation on every render
  const generateCalendar = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    let firstDayWeekday = firstDayOfMonth.getDay() - 1;
    if (firstDayWeekday === -1) firstDayWeekday = 6;

    const daysInMonth = lastDayOfMonth.getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const calendar: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Previous month days
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, daysInPrevMonth - i);
      calendar.push({
        date,
        shifts: getShiftsForDate(date),
        isCurrentMonth: false,
        isToday: false,
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendar.push({
        date,
        shifts: getShiftsForDate(date),
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
      });
    }

    // Next month days to complete the grid
    const remainingDays = 42 - calendar.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      calendar.push({
        date,
        shifts: getShiftsForDate(date),
        isCurrentMonth: false,
        isToday: false,
      });
    }

    setCalendarDays(calendar);
  }, [currentDate, getShiftsForDate]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  useEffect(() => {
    if (!loading) {
      generateCalendar();
    }
  }, [loading, generateCalendar]);

  // Carica lista sedi disponibili
  useEffect(() => {
    const loadSites = async () => {
      try {
        const filter: SiteFilter = {
          isActive: true,
          pageSize: 100,
        };

        // Owner vede solo le sedi della propria company
        if (user?.role === 1 && user?.companyId) {
          filter.companyId = user.companyId;
        }

        const response = await siteService.getSites(filter);
        setSites(response.data);
      } catch (err: unknown) {
        console.error('Errore nel caricamento delle sedi:', err);
      }
    };

    // Carica sedi solo per Owner e SuperAdmin
    if (user && user.role <= 1) {
      loadSites();
    }
  }, [user?.role, user?.companyId]);

  // Carica lista dipendenti
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const filter: { role: number; active: boolean; pageSize: number } = {
          role: 3, // Solo Employee
          active: true,
          pageSize: 100,
        };

        const response = await userService.getUsers(filter);
        setEmployees(response.data);
      } catch (err: unknown) {
        console.error('Errore nel caricamento dei dipendenti:', err);
      }
    };

    // Carica dipendenti solo per non-employee
    if (user && user.role <= 2) {
      loadEmployees();
    }
  }, [user?.role]);

  // Memoize navigation handlers
  const previousMonth = useCallback(() => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  }, [currentDate]);

  const nextMonth = useCallback(() => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  }, [currentDate]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Memoize getShiftStatusColor using constants
  const getShiftStatusColor = useCallback((status: string): string => {
    return SHIFT_STATUS_COLORS[status] || '#95a5a6';
  }, []);

  // Memoize handleDayClick
  const handleDayClick = useCallback((day: CalendarDay) => {
    if (day.shifts.length > 0) {
      setSelectedDay(day);
    }
  }, []);

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <h1>📅 Calendario Turni</h1>
        <button className={styles.todayButton} onClick={goToToday}>
          📍 Oggi
        </button>
      </div>

      <div className={styles.controls}>
        <button className={styles.navButton} onClick={previousMonth}>
          ← Precedente
        </button>
        <h2 className={styles.monthYear}>
          {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button className={styles.navButton} onClick={nextMonth}>
          Successivo →
        </button>
      </div>

      {/* Filtri */}
      {user && user.role <= 2 && (
        <div className={styles.filters}>
          {user.role <= 1 && sites.length > 0 && (
            <div className={styles.filterGroup}>
              <label htmlFor="site">Sede:</label>
              <select
                id="site"
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value ? parseInt(e.target.value) : '')}
                className={styles.select}
              >
                <option value="">Tutte le sedi</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {employees.length > 0 && (
            <div className={styles.filterGroup}>
              <label htmlFor="employee">Dipendente:</label>
              <select
                id="employee"
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value ? parseInt(e.target.value) : '')}
                className={styles.select}
              >
                <option value="">Tutti i dipendenti</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento calendario...</div>
      ) : (
        <div className={styles.calendarGrid}>
          {/* Day headers */}
          {DAY_NAMES.map(day => (
            <div key={day} className={styles.dayHeader}>
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`${styles.calendarDay} ${
                !day.isCurrentMonth ? styles.otherMonth : ''
              } ${day.isToday ? styles.today : ''}`}
              onClick={() => handleDayClick(day)}
            >
              <div className={styles.dayNumber}>{day.date.getDate()}</div>
              <div className={styles.shiftsContainer}>
                {day.shifts.slice(0, 3).map(shift => (
                  <div
                    key={shift.id}
                    className={styles.shiftIndicator}
                    style={{ backgroundColor: getShiftStatusColor(shift.status) }}
                    title={`${shift.employeeName} - ${shift.role} - ${shift.siteName} - ${formatTime(shift.startTime)}-${formatTime(shift.endTime)}`}
                  >
                    <span className={styles.shiftTime}>
                      {formatTime(shift.startTime)}
                    </span>
                    <span className={styles.shiftEmployee}>
                      👤 {shift.employeeName?.split(' ')[0]}
                    </span>
                    <span className={styles.shiftSite}>
                      🏢 {shift.siteName?.substring(0, 10)}
                    </span>
                    <span className={styles.shiftRole}>
                      👔 {shift.role}
                    </span>
                  </div>
                ))}
                {day.shifts.length > 3 && (
                  <div className={styles.moreShifts}>
                    +{day.shifts.length - 3} altro{day.shifts.length - 3 > 1 ? 'i' : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedDay && (
        <div className={styles.modalOverlay} onClick={() => setSelectedDay(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Turni del {formatLongDate(selectedDay.date)}</h2>
              <button
                className={styles.closeButton}
                onClick={() => setSelectedDay(null)}
              >
                ❌
              </button>
            </div>
            <div className={styles.modalContent}>
              {selectedDay.shifts.length === 0 ? (
                <p className={styles.noShifts}>Nessun turno in questo giorno</p>
              ) : (
                <div className={styles.shiftsList}>
                  {selectedDay.shifts.map(shift => (
                    <div key={shift.id} className={styles.shiftDetail}>
                      <div className={styles.shiftDetailHeader}>
                        <span
                          className={styles.shiftDetailStatus}
                          style={{ backgroundColor: getShiftStatusColor(shift.status) }}
                        >
                          {shift.status}
                        </span>
                        <span className={styles.shiftDetailId}>#{shift.id}</span>
                      </div>
                      <div className={styles.shiftDetailInfo}>
                        <div>
                          <strong>👤 Dipendente:</strong> {shift.employeeName}
                        </div>
                        <div>
                          <strong>🕐 Orario:</strong> {formatTime(shift.startTime)} -{' '}
                          {formatTime(shift.endTime)}
                        </div>
                        <div>
                          <strong>🏢 Sede:</strong> {shift.siteName}
                        </div>
                        <div>
                          <strong>👔 Ruolo:</strong> {shift.role}
                        </div>
                        {shift.actualStartTime && (
                          <div>
                            <strong>✅ Clock In:</strong>{' '}
                            {formatTime(shift.actualStartTime)}
                          </div>
                        )}
                        {shift.actualEndTime && (
                          <div>
                            <strong>🏁 Clock Out:</strong>{' '}
                            {formatTime(shift.actualEndTime)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Calendar.displayName = 'Calendar';
